import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

/* ── Brand assets (PNG lockups: the SVG wordmarks rely on an external Google
   Font, which does not load when an SVG is used via <img>, so PNG is safest) ── */
export const LOGO_LIGHT = '/brand/logo-horizontal.png';      // for light backgrounds
export const LOGO_DARK  = '/brand/logo-horizontal-on-dark.png'; // for the dark footer

/* ── Ember palette (from brand-assets/README) ── */
export const FLAME   = '#ff5a2c';
export const FLAME_2 = '#ff7a52';
export const GOLD    = '#FFC53D';
export const INK     = '#15130f';
export const INK_2   = '#2a2620';
export const CREAM   = '#f7f6f3';
export const BODY    = '#5f5a51';
export const MUTED   = '#9a948a';
export const BORDER  = '#e6e1d7';
export const CARD    = 'rgba(255,255,255,.85)';

export const FONT = "'Space Grotesk', 'Segoe UI', Arial, sans-serif";
export const MONO = "'Space Mono', ui-monospace, monospace";

/* Back-compat: older pages (Privacy, Terms, Login) still import these names.
   They are remapped onto the ember palette so nothing breaks mid-migration. */
export const PRIMARY = FLAME;
export const PRIMARY_DARK = '#e04a1f';
export const PRIMARY_LIGHT = '#fdece6';
export const DARK = INK;
export const LOGO = LOGO_LIGHT;
export const BG_ELEVATED = '#efece5';
export const ACCENT = GOLD;

/* ── Animated node-network background (ported from the design's canvas) ──
   Respects prefers-reduced-motion; purely decorative, aria-hidden. */
export function NetworkCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let w = 0, h = 0, nodes = [], packets = [], scrollY = 0, t = 0, raf = null;
    const D = 150;
    const LABELS = ['SSP', 'DSP', 'PUB', 'APP', 'ADX', 'OpenRTB', 'schain'];

    const build = () => {
      const count = 80;
      nodes = [];
      for (let i = 0; i < count; i++) {
        nodes.push({
          x: Math.random() * w, y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.16, vy: (Math.random() - 0.5) * 0.16,
          r: Math.random() < 0.16 ? 3.0 : 1.5,
          accent: Math.random() < 0.22,
          hub: Math.random() < 0.12,
          label: LABELS[(Math.random() * LABELS.length) | 0],
        });
      }
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth; h = window.innerHeight;
      cv.width = w * dpr; cv.height = h * dpr;
      cv.style.width = w + 'px'; cv.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    };

    const onScroll = () => { scrollY = window.scrollY || document.documentElement.scrollTop || 0; };

    const drawScene = () => {
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < D) {
            ctx.strokeStyle = 'rgba(45,40,32,' + ((1 - dist / D) * 0.15) + ')';
            ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          }
        }
      }
      for (const p of packets) {
        const a = nodes[p.i], b = nodes[p.j];
        if (!a || !b) continue;
        const x = a.x + (b.x - a.x) * p.p, y = a.y + (b.y - a.y) * p.p;
        ctx.fillStyle = 'rgba(255,90,44,' + (0.9 * (1 - Math.abs(0.5 - p.p) * 1.2)) + ')';
        ctx.beginPath(); ctx.arc(x, y, 2.2, 0, Math.PI * 2); ctx.fill();
      }
      ctx.font = '9px "Space Mono", monospace';
      for (const n of nodes) {
        ctx.fillStyle = n.accent ? 'rgba(255,90,44,0.55)' : 'rgba(60,54,44,0.28)';
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2); ctx.fill();
        if (n.hub) {
          ctx.strokeStyle = 'rgba(255,90,44,0.22)'; ctx.lineWidth = 1;
          ctx.beginPath(); ctx.arc(n.x, n.y, n.r + 5, 0, Math.PI * 2); ctx.stroke();
          ctx.fillStyle = 'rgba(90,84,74,0.42)';
          ctx.fillText(n.label, n.x + 8, n.y + 3);
        }
      }
    };

    const loop = () => {
      t++;
      ctx.clearRect(0, 0, w, h);
      for (const n of nodes) {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0) n.x += w; if (n.x >= w) n.x -= w;
        if (n.y < 0) n.y += h; if (n.y >= h) n.y -= h;
      }
      for (const p of packets) p.p += p.sp;
      for (let i = packets.length - 1; i >= 0; i--) if (packets[i].p >= 1) packets.splice(i, 1);
      if (t % 6 === 0 && packets.length < 30) {
        const a = (Math.random() * nodes.length) | 0;
        for (let j = 0; j < nodes.length; j++) {
          if (j === a) continue;
          const dx = nodes[a].x - nodes[j].x, dy = nodes[a].y - nodes[j].y;
          if (Math.sqrt(dx * dx + dy * dy) < D) {
            packets.push({ i: a, j, p: 0, sp: 0.01 + Math.random() * 0.018 });
            break;
          }
        }
      }
      const par = scrollY * 0.15;
      let oy = -(par % h); if (oy > 0) oy -= h;
      for (let k = 0; k < 2; k++) { ctx.save(); ctx.translate(0, oy + k * h); drawScene(); ctx.restore(); }
      raf = requestAnimationFrame(loop);
    };

    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('scroll', onScroll, { passive: true });

    if (reduce) drawScene();
    else raf = requestAnimationFrame(loop);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}
    />
  );
}

/* Shared button styles */
export const btnInk = {
  display: 'inline-block', background: INK, color: '#f6f5f2',
  fontSize: '17px', fontWeight: 500, padding: '15px 32px',
  borderRadius: '30px', textDecoration: 'none',
};
export const btnFlame = {
  display: 'inline-block', background: FLAME, color: INK,
  fontSize: '17px', fontWeight: 600, padding: '16px 34px',
  borderRadius: '30px', textDecoration: 'none',
};
export const btnGhost = {
  display: 'inline-block', border: '1px solid #c9c4ba', color: INK,
  fontSize: '17px', padding: '15px 28px', borderRadius: '30px',
  background: 'rgba(255,255,255,.5)', textDecoration: 'none',
};

export function SiteLayout({ children }) {
  const { pathname } = useLocation();

  // The dashboard shell locks scrolling; the marketing site needs it free.
  useEffect(() => {
    const root = document.getElementById('root');
    const prev = {
      rO: root?.style.overflow, rH: root?.style.height,
      bO: document.body.style.overflow, bH: document.body.style.height,
      hO: document.documentElement.style.overflow, hH: document.documentElement.style.height,
    };
    if (root) { root.style.overflow = 'auto'; root.style.height = 'auto'; }
    document.body.style.overflow = 'auto'; document.body.style.height = 'auto';
    document.documentElement.style.overflow = 'auto'; document.documentElement.style.height = 'auto';
    return () => {
      if (root) { root.style.overflow = prev.rO || ''; root.style.height = prev.rH || ''; }
      document.body.style.overflow = prev.bO || '';
      document.body.style.height = prev.bH || '';
      document.documentElement.style.overflow = prev.hO || '';
      document.documentElement.style.height = prev.hH || '';
    };
  }, []);

  const navLink = (to, label) => (
    <Link
      key={label}
      to={to}
      className="ia-nav-link"
      style={{
        fontSize: '14px', textDecoration: 'none',
        color: pathname === to ? FLAME : BODY,
        fontWeight: pathname === to ? 600 : 400,
      }}
    >
      {label}
    </Link>
  );

  return (
    <div style={{
      position: 'relative', minHeight: '100vh',
      background: 'linear-gradient(180deg,#f7f6f3 0%,#efece5 60%,#f2efe8 100%)',
      fontFamily: FONT, color: INK, overflowX: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap');
        * { box-sizing: border-box; }
        .ia-nav-link:hover { color: ${FLAME} !important; }
        .ia-btn:hover { opacity: .9; }
        input::placeholder, textarea::placeholder { color: #a8a297; }
        input:focus, select:focus, textarea:focus { outline: none; border-color: ${FLAME} !important; }
        @media (max-width: 900px) {
          .ia-grid-3 { grid-template-columns: 1fr !important; }
          .ia-grid-2 { grid-template-columns: 1fr !important; }
          .ia-nav-links { gap: 14px !important; }
          .ia-hero-h1 { font-size: 40px !important; }
        }
      `}</style>

      <NetworkCanvas />

      <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>

        {/* ── NAV ── */}
        <header style={{
          position: 'sticky', top: 0, zIndex: 20,
          backdropFilter: 'blur(10px)', background: 'rgba(247,246,243,.72)',
          borderBottom: '1px solid rgba(216,212,203,.7)',
        }}>
          <nav style={{
            maxWidth: '1180px', margin: '0 auto', padding: '16px 32px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
          }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
              <img src={LOGO_LIGHT} alt="IgnitorAds" style={{ height: '48px', width: 'auto', objectFit: 'contain', display: 'block' }} />
              <span style={{
                fontFamily: MONO, fontSize: '12px', color: MUTED, whiteSpace: 'nowrap',
                border: `1px solid #d8d4cb`, padding: '5px 10px', borderRadius: '5px',
              }}>AdTech Hub</span>
            </Link>

            <div className="ia-nav-links" style={{ display: 'flex', gap: '28px', alignItems: 'center', flexWrap: 'wrap' }}>
              {navLink('/products', 'Tools')}
              {navLink('/contact', 'Pricing')}
              <Link to="/contact#contact" className="ia-nav-link" style={{ fontSize: '14px', color: BODY, textDecoration: 'none' }}>Contact</Link>
              <Link to="/login" className="ia-nav-link" style={{ fontSize: '14px', color: BODY, textDecoration: 'none' }}>Sign in</Link>
              <Link to="/contact" className="ia-btn" style={{
                fontSize: '14px', color: '#f6f5f2', background: INK,
                padding: '9px 18px', borderRadius: '24px', fontWeight: 500, textDecoration: 'none',
              }}>Get started</Link>
            </div>
          </nav>
        </header>

        <main style={{ flex: 1 }}>{children}</main>

        {/* ── FOOTER ── */}
        <footer style={{ background: INK, color: '#f6f5f2', borderTop: `1px solid ${INK_2}` }}>
          <div style={{
            maxWidth: '1180px', margin: '0 auto', padding: '28px 32px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: '18px', flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <img src={LOGO_DARK} alt="IgnitorAds" style={{ height: '34px', width: 'auto', objectFit: 'contain', display: 'block' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '13px', color: '#8a847a' }}>
                  © {new Date().getFullYear()} IgnitorAds — AdTech Hub
                </span>
                {/* Cross-domain brand link: tells Google .co is a product OF the .com
                    brand hub. Plain <a> (not <Link>) because it leaves this domain. */}
                <a
                  href="https://www.ignitorads.com"
                  target="_blank"
                  rel="noopener"
                  style={{ fontSize: '13px', color: '#8a847a', textDecoration: 'none', width: 'fit-content' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#f6f5f2'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#8a847a'; }}
                >
                  An IgnitorAds product →
                </a>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              {[
                { to: '/products', label: 'Tools' },
                { to: '/contact', label: 'Pricing' },
                { to: '/contact#contact', label: 'Contact' },
                { to: '/privacy', label: 'Privacy Policy' },
                { to: '/terms', label: 'Terms of Service' },
              ].map(({ to, label }) => (
                <Link
                  key={label}
                  to={to}
                  style={{ fontSize: '13px', color: '#8a847a', textDecoration: 'none' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#f6f5f2'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = '#8a847a'; }}
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
